import { useState } from "react";
import { motion } from "framer-motion";
import { User, Heart, LogOut, Mail, Send, Loader2, Check, Link2, Copy } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import NotificationSettings from "@/components/NotificationSettings";

export default function ProfilePage() {
  const { user, profile, workspace, partnerProfile, signOut } = useAuth();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [invited, setInvited] = useState(false);

  // Linking code state
  const [linkCode, setLinkCode] = useState<string | null>(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joiningByCode, setJoiningByCode] = useState(false);

  const sendPartnerInvite = async () => {
    if (!inviteEmail.trim() || !workspace) return;
    setInviting(true);
    try {
      const { error } = await supabase.from("workspace_invites").insert({
        workspace_id: workspace.id,
        invited_by: user!.id,
        invited_email: inviteEmail.trim().toLowerCase(),
      });
      if (error) throw error;

      supabase.functions.invoke("send-invite-email", {
        body: {
          partnerEmail: inviteEmail.trim().toLowerCase(),
          inviterName: profile?.preferred_name || profile?.full_name || "Your partner",
          coupleName: workspace.couple_name,
          siteUrl: window.location.origin,
        },
      }).catch(console.error);

      setInvited(true);
      toast.success("Invite sent 💛", { description: `We've sent an invitation to ${inviteEmail}` });
    } catch (err: any) {
      toast.error("Couldn't send invite", { description: err.message });
    } finally {
      setInviting(false);
    }
  };

  const generateLinkCode = async () => {
    if (!workspace) return;
    setGeneratingCode(true);
    try {
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      const { error } = await supabase.from("workspace_links").insert({
        code,
        workspace_id: workspace.id,
        created_by: user!.id,
        expires_at: expiresAt,
      });
      if (error) throw error;
      setLinkCode(code);
      toast.success("Link code generated! Share it with your partner.");
    } catch (err: any) {
      toast.error("Couldn't generate code", { description: err.message });
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyCode = () => {
    if (linkCode) {
      navigator.clipboard.writeText(linkCode);
      toast.success("Code copied!");
    }
  };

  const joinByCode = async () => {
    if (!joinCode.trim()) return;
    setJoiningByCode(true);
    try {
      const { data, error } = await supabase.functions.invoke("validate-workspace-code", {
        body: { code: joinCode.trim(), action: "join", full_name: profile?.full_name || "User" },
      });
      if (error) throw error;
      if (!data?.valid) { toast.error(data?.error || "Invalid or expired code"); setJoiningByCode(false); return; }
      toast.success("Linked! Refreshing...");
      window.location.reload();
    } catch (err: any) {
      toast.error("Couldn't join", { description: err.message });
    } finally {
      setJoiningByCode(false);
    }
  };

  return (
    <div className="space-y-8 max-w-full">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl md:text-4xl font-display font-semibold text-foreground">Profile</h1>
        <p className="mt-2 text-muted-foreground text-lg">Your personal details and preferences.</p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Your Info */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card border border-border rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-2 text-primary">
            <User className="w-5 h-5" />
            <h2 className="font-display text-xl font-semibold">You</h2>
          </div>
          <div className="space-y-3">
            <div><p className="text-xs text-muted-foreground">Name</p><p className="text-sm text-foreground font-medium">{profile?.full_name || "—"}</p></div>
            {profile?.preferred_name && (<div><p className="text-xs text-muted-foreground">Goes by</p><p className="text-sm text-foreground font-medium">{profile.preferred_name}</p></div>)}
            <div><p className="text-xs text-muted-foreground">Email</p><p className="text-sm text-foreground">{profile?.email}</p></div>
            {profile?.personal_intention && (<div><p className="text-xs text-muted-foreground">Intention</p><p className="text-sm text-foreground italic">"{profile.personal_intention}"</p></div>)}
          </div>
        </motion.div>

        {/* Partner Info */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-card border border-border rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-2 text-accent">
            <Heart className="w-5 h-5" />
            <h2 className="font-display text-xl font-semibold">Your Partner</h2>
          </div>
          {partnerProfile ? (
            <div className="space-y-3">
              <div><p className="text-xs text-muted-foreground">Name</p><p className="text-sm text-foreground font-medium">{partnerProfile.full_name || "—"}</p></div>
              {partnerProfile.preferred_name && (<div><p className="text-xs text-muted-foreground">Goes by</p><p className="text-sm text-foreground font-medium">{partnerProfile.preferred_name}</p></div>)}
              <div><p className="text-xs text-muted-foreground">Email</p><p className="text-sm text-foreground">{partnerProfile.email}</p></div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Your partner hasn't joined yet.</p>

              {/* Email invite */}
              {invited ? (
                <div className="flex items-center gap-2 text-primary text-sm"><Check className="w-4 h-4" /><span>Invite sent to {inviteEmail}</span></div>
              ) : (
                <div className="flex gap-2">
                  <div className="flex-1 relative min-w-0">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="partner@email.com" className="w-full bg-background border border-input rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" onKeyDown={(e) => e.key === "Enter" && sendPartnerInvite()} />
                  </div>
                  <button onClick={sendPartnerInvite} disabled={inviting || !inviteEmail.trim()} className="px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2 flex-shrink-0">
                    {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Invite
                  </button>
                </div>
              )}

              {/* Linking code */}
              <div className="border-t border-border pt-4 space-y-3">
                <p className="text-xs text-muted-foreground font-medium">Or share a linking code</p>
                {linkCode ? (
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-lg font-bold text-foreground tracking-widest">{linkCode}</span>
                    <button onClick={copyCode} className="p-2 bg-secondary rounded-lg hover:bg-secondary/80"><Copy className="w-4 h-4 text-muted-foreground" /></button>
                    <span className="text-xs text-muted-foreground">Expires in 24h</span>
                  </div>
                ) : (
                  <button onClick={generateLinkCode} disabled={generatingCode} className="flex items-center gap-2 px-3 py-2 bg-secondary text-secondary-foreground rounded-lg text-sm hover:bg-secondary/80 disabled:opacity-50">
                    {generatingCode ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                    Generate Code
                  </button>
                )}
              </div>

              {/* Join by code */}
              <div className="border-t border-border pt-4 space-y-3">
                <p className="text-xs text-muted-foreground font-medium">Have a code from your partner?</p>
                <div className="flex gap-2">
                  <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="Enter code" className="flex-1 min-w-0 bg-background border border-input rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-ring/40" maxLength={6} />
                  <button onClick={joinByCode} disabled={joiningByCode || !joinCode.trim()} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium disabled:opacity-50 flex-shrink-0">
                    {joiningByCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <NotificationSettings />

      {workspace && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-sage-light border border-border rounded-xl p-6 space-y-3">
          <h2 className="font-display text-xl font-semibold text-foreground">{workspace.couple_name}</h2>
          {workspace.relationship_stage && <p className="text-sm text-muted-foreground">Stage: {workspace.relationship_stage}</p>}
          {workspace.intentions && workspace.intentions.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {workspace.intentions.map((i) => (
                <span key={i} className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">{i}</span>
              ))}
            </div>
          )}
        </motion.div>
      )}

      <button onClick={signOut} className="flex items-center gap-2 px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <LogOut className="w-4 h-4" /> Sign Out
      </button>
    </div>
  );
}
